import { isDesktop } from './platform';

// The spec's "scrivar.com/billing" maps to the real in-app billing route,
// which has inline auth and the Stripe checkout buttons.
export const SCRIVAR_BILLING_URL = 'https://scrivar.com/account/billing';

/**
 * Only ever hand the system browser an https URL on our own domain. Used by
 * both the renderer helper and (mirrored) the desktop main-process handler.
 */
export function isAllowedExternalUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'https:'
      && u.username === ''
      && u.password === ''
      && (u.hostname === 'scrivar.com' || u.hostname.endsWith('.scrivar.com'));
  } catch {
    return false;
  }
}

/**
 * Open an external URL in the system browser.
 *   - Desktop: via the validated openExternal IPC bridge (will-navigate blocks
 *     in-window navigation, so window.location.href would not work).
 *   - Web: a normal new tab.
 * Returns false if the URL isn't an allowed scrivar.com https URL.
 */
export function openExternalUrl(url) {
  if (!isAllowedExternalUrl(url)) return false;
  if (isDesktop() && window.electronAPI?.openExternal) {
    window.electronAPI.openExternal(url).catch(() => { /* non-fatal */ });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return true;
}

/**
 * Only allow http/https/mailto schemes through openUserContentUrl. PDF authors
 * link anywhere, so (unlike isAllowedExternalUrl) this deliberately does NOT
 * scope by domain — it exists purely to reject dangerous schemes such as
 * javascript:/file:/data:.
 */
function isAllowedUserContentUrl(url) {
  try {
    const u = new URL(url);
    const scheme = u.protocol.toLowerCase();
    return scheme === 'http:' || scheme === 'https:' || scheme === 'mailto:';
  } catch {
    return false;
  }
}

/**
 * Open an arbitrary user-content URL (PDF link annotations) in the system
 * browser. Unlike openExternalUrl (billing CTAs, scrivar.com-scoped), this
 * validates SCHEME only — http/https/mailto — because PDF authors link
 * anywhere. javascript:/file:/data:/etc are rejected.
 */
export function openUserContentUrl(url) {
  if (!isAllowedUserContentUrl(url)) return false;
  if (isDesktop() && window.electronAPI?.openExternal) {
    window.electronAPI.openExternal(url).catch(() => { /* non-fatal */ });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return true;
}
